/**
 * Trend charts for workflow suite benchmarks: duration, tokens, pass/fail, and node results.
 *
 * @module pages/WorkflowSuiteTrendCharts
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Box, Button, Flex, Heading, Card, CardContent, Select, LineChart, StackedBarChart, ChartEmptyState, LoadingOrbit } from '@chatsdet/ui';
import { useWorkflowSuiteTrendsQuery, useBenchmarkConfigsQuery } from '../useBenchmarkData';
import { useWorkflowSuiteSearch, parseRunNumbers } from './useWorkflowSuiteSearch';
import { WorkflowColorSelect, workflowColorAt, type WorkflowColor } from './WorkflowColorSelect';
import {
  prepareChartData,
  transformTrendsForDurationChart,
  transformTrendsForTokenChart,
  transformTrendsForStatusChart,
  transformTrendsForNodeChart,
  transformTrendsForCachedStepsChart,
  getUniqueWorkflowNames,
  getAvailableRunNumbers,
  sortTrendsByDate,
  stripBenchmarkPrefix,
} from './useWorkflowSuiteTransforms';

const DAYS_OPTIONS = [
  { id: '7', label: '7 days' },
  { id: '14', label: '14 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];

const TRIGGER_OPTIONS = [
  { id: 'all', label: 'All triggers' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'manual', label: 'Manual' },
];

const COLOR_SUCCESS = 'hsl(var(--success))';
const COLOR_FAIL = 'hsl(var(--destructive))';
const COLOR_SKIPPED = 'hsl(var(--muted))';
const COLOR_CACHED = 'hsl(210, 70%, 55%)';
const COLOR_L2_RECOVERY = 'hsl(var(--warning))';
const COLOR_EJECTED = 'hsl(25, 90%, 55%)';
const COLOR_AGENT = 'hsl(280, 60%, 55%)';

function runNumbers(max: number): number[] {
  return Array.from({ length: max }, (_, i) => i + 1);
}

function runLabel(n: number): string {
  return n === 1 ? `Run ${n} (uncached)` : `Run ${n} (cached)`;
}

function buildStatusStacks(maxRunNumber: number) {
  return runNumbers(maxRunNumber).flatMap((i) => [
    { dataKey: `run${i}_pass`, name: `${runLabel(i)} Passed`, color: COLOR_SUCCESS },
    { dataKey: `run${i}_fail`, name: `${runLabel(i)} Failed`, color: COLOR_FAIL },
  ]);
}

function buildNodeStacks(maxRunNumber: number) {
  return runNumbers(maxRunNumber).flatMap((i) => [
    { dataKey: `run${i}_success`, name: `${runLabel(i)} Success`, color: COLOR_SUCCESS },
    { dataKey: `run${i}_fail`, name: `${runLabel(i)} Failed`, color: COLOR_FAIL },
    { dataKey: `run${i}_skipped`, name: `${runLabel(i)} Skipped`, color: COLOR_SKIPPED },
  ]);
}

function buildCachedStepsStacks(maxRunNumber: number) {
  return runNumbers(maxRunNumber).flatMap((i) => [
    { dataKey: `run${i}_cached`, name: `${runLabel(i)} Cached`, color: COLOR_CACHED },
    { dataKey: `run${i}_l2Recovery`, name: `${runLabel(i)} L2 Recovery`, color: COLOR_L2_RECOVERY },
    { dataKey: `run${i}_ejected`, name: `${runLabel(i)} Ejected`, color: COLOR_EJECTED },
    { dataKey: `run${i}_agent`, name: `${runLabel(i)} Agent`, color: COLOR_AGENT },
    { dataKey: `run${i}_skipped`, name: `${runLabel(i)} Skipped`, color: COLOR_SKIPPED },
  ]);
}

function ColorDot({ color, className }: { color: string; className?: string }) {
  return <div className={className ?? 'w-2.5 h-2.5 rounded-sm'} style={{ backgroundColor: color }} />;
}

function StatusTooltip({ data, maxRunNumber }: { data: Record<string, unknown>; maxRunNumber: number }) {
  return (
    <div className="space-y-1">
      <div className="font-medium text-sm">{String(data.date)}</div>
      {runNumbers(maxRunNumber).map((n) => {
        const pass = Number(data[`run${n}_pass`] ?? 0);
        const fail = Number(data[`run${n}_fail`] ?? 0);
        if (pass === 0 && fail === 0) return null;
        const parts: string[] = [];
        if (pass > 0) parts.push(`${pass} passed`);
        if (fail > 0) parts.push(`${fail} failed`);
        return (
          <div key={n} className="flex items-center gap-2 text-xs">
            <ColorDot color={fail > 0 ? COLOR_FAIL : COLOR_SUCCESS} />
            <span>{runLabel(n)}: {parts.join(', ')}</span>
          </div>
        );
      })}
    </div>
  );
}

type ChartCategory = { key: string; color: string; label: string };

const NODE_CATEGORIES: ChartCategory[] = [
  { key: 'success', color: COLOR_SUCCESS, label: 'Passed' },
  { key: 'fail', color: COLOR_FAIL, label: 'Failed' },
  { key: 'skipped', color: COLOR_SKIPPED, label: 'Skipped' },
];

const CACHE_CATEGORIES: ChartCategory[] = [
  { key: 'cached', color: COLOR_CACHED, label: 'Cached' },
  { key: 'l2Recovery', color: COLOR_L2_RECOVERY, label: 'L2 Recovery' },
  { key: 'ejected', color: COLOR_EJECTED, label: 'Ejected' },
  { key: 'agent', color: COLOR_AGENT, label: 'Agent' },
  { key: 'skipped', color: COLOR_SKIPPED, label: 'Skipped' },
];

function CategoryTooltip({
  data, maxRunNumber, categories,
}: {
  data: Record<string, unknown>;
  maxRunNumber: number;
  categories: ChartCategory[];
}) {
  return (
    <div className="space-y-1">
      <div className="font-medium text-sm">{String(data.date)}</div>
      {runNumbers(maxRunNumber).map((n) => {
        const values = categories.map((c) => ({ ...c, count: Number(data[`run${n}_${c.key}`] ?? 0) }));
        if (values.every((v) => v.count === 0)) return null;
        return (
          <div key={n} className="text-xs">
            <div className="font-medium">{runLabel(n)}</div>
            <div className="flex items-center gap-3 ml-3">
              {values.filter((v) => v.count > 0).map((v) => (
                <span key={v.key} className="flex items-center gap-1">
                  <ColorDot color={v.color} className="w-2 h-2 rounded-sm" />
                  {v.count} {v.label.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StackedChartCard({
  title, result, stacks, legendItems, emptyMessage, onBarClick, renderTooltip,
}: {
  title: string;
  result: { data: Record<string, string | number>[]; maxRunNumber: number };
  stacks: { dataKey: string; name: string; color: string }[];
  legendItems: { color: string; label: string }[];
  emptyMessage: string;
  onBarClick: (data: Record<string, unknown>) => void;
  renderTooltip: (data: Record<string, unknown>) => React.ReactNode;
}) {
  return (
    <Card padding="md">
      <CardContent>
        <Heading level={5} className="mb-4">{title}</Heading>
        {result.data.length > 0 ? (
          <>
            <StackedBarChart
              data={result.data}
              xDataKey="date"
              stacks={stacks}
              height={200}
              showLegend={false}
              showXAxis
              allowDecimals={false}
              onBarClick={onBarClick}
              renderTooltip={renderTooltip}
            />
            <ChartLegend items={legendItems} />
          </>
        ) : (
          <ChartEmptyState message={emptyMessage} />
        )}
      </CardContent>
    </Card>
  );
}

function ChartLegend({ items }: { items: { color: string; label: string }[] }) {
  return (
    <Flex gap="md" className="mt-2 justify-center">
      {items.map((item) => (
        <Flex key={item.label} gap="xs" align="center">
          <ColorDot color={item.color} className="w-3 h-3 rounded-sm" />
          <span className="text-xs text-muted-foreground">{item.label}</span>
        </Flex>
      ))}
    </Flex>
  );
}

export function WorkflowSuiteTrendCharts() {
  const { search, updateSearch } = useWorkflowSuiteSearch();
  const days = search.days ?? 30;
  const triggerFilter = search.trigger ?? 'all';
  const configFilter = search.configId ?? 'all';
  const selectedRuns = React.useMemo(() => parseRunNumbers(search.runs), [search.runs]);
  // Empty selection means "all workflows" — mirrors the runs filter semantics.
  const selectedWorkflows = React.useMemo(() => search.workflows ?? [], [search.workflows]);
  const isAllWorkflows = selectedWorkflows.length === 0;
  const selectedWorkflowSet = React.useMemo(() => new Set(selectedWorkflows), [selectedWorkflows]);

  const configsQuery = useBenchmarkConfigsQuery({ jobType: 'workflow_suite' });
  const workflowSuiteConfigs = configsQuery.data?.configs ?? [];

  const trendsQuery = useWorkflowSuiteTrendsQuery({
    days,
    configId: configFilter === 'all' ? undefined : configFilter,
  });

  const allTrends = trendsQuery.data?.trends ?? [];
  const trends = React.useMemo(() => {
    const filtered = triggerFilter === 'all'
      ? allTrends
      : allTrends.filter((t) => t.triggerType === triggerFilter);
    return sortTrendsByDate(filtered);
  }, [allTrends, triggerFilter]);
  const workflowNames = React.useMemo(() => getUniqueWorkflowNames(allTrends), [allTrends]);
  const availableRunNumbers = React.useMemo(() => getAvailableRunNumbers(trends), [trends]);
  const workflowFilterNames = React.useMemo(
    () => (isAllWorkflows ? undefined : selectedWorkflows),
    [isAllWorkflows, selectedWorkflows],
  );

  // Color per workflow, keyed by position in the FULL sorted list (workflowNames
  // from allTrends, not the trigger-filtered set) so a workflow keeps its color
  // as filters change. Every charted line and pill name is a subset of this list,
  // so colorMap lookups always resolve. Shared with the line charts so the
  // selector reads as the legend — stable for a given workflow set.
  const colorMap = React.useMemo(
    () => new Map<string, WorkflowColor>(workflowNames.map((n, i) => [n, workflowColorAt(i)])),
    [workflowNames],
  );

  const prepared = React.useMemo(
    () => prepareChartData(trends, workflowFilterNames, selectedRuns),
    [trends, workflowFilterNames, selectedRuns],
  );
  const durationData = React.useMemo(() => transformTrendsForDurationChart(prepared), [prepared]);
  const tokenData = React.useMemo(() => transformTrendsForTokenChart(prepared), [prepared]);
  const statusResult = React.useMemo(() => transformTrendsForStatusChart(prepared), [prepared]);
  const nodeResult = React.useMemo(() => transformTrendsForNodeChart(prepared), [prepared]);
  const cachedStepsResult = React.useMemo(() => transformTrendsForCachedStepsChart(prepared), [prepared]);

  const statusStacks = React.useMemo(
    () => buildStatusStacks(statusResult.maxRunNumber),
    [statusResult.maxRunNumber],
  );
  const nodeStacks = React.useMemo(
    () => buildNodeStacks(nodeResult.maxRunNumber),
    [nodeResult.maxRunNumber],
  );
  const cachedStepsStacks = React.useMemo(
    () => buildCachedStepsStacks(cachedStepsResult.maxRunNumber),
    [cachedStepsResult.maxRunNumber],
  );

  const filteredWorkflowNames = React.useMemo(() => getUniqueWorkflowNames(trends), [trends]);
  const lineNames = React.useMemo(
    () =>
      isAllWorkflows
        ? filteredWorkflowNames
        : filteredWorkflowNames.filter((n) => selectedWorkflowSet.has(n)),
    [isAllWorkflows, filteredWorkflowNames, selectedWorkflowSet],
  );

  const lines = React.useMemo(
    () => lineNames.map((name) => ({
      dataKey: name,
      name: stripBenchmarkPrefix(name),
      color: colorMap.get(name)?.solid,
    })),
    [lineNames, colorMap],
  );

  const toggleRunNumber = React.useCallback((n: number) => {
    const isAllSelected = selectedRuns.length === 0;
    let next: number[];
    if (isAllSelected) {
      next = availableRunNumbers.filter((r) => r !== n);
    } else if (selectedRuns.includes(n)) {
      next = selectedRuns.filter((r) => r !== n);
    } else {
      next = [...selectedRuns, n].sort((a, b) => a - b);
    }
    if (next.length === 0 || next.length === availableRunNumbers.length) {
      updateSearch({ runs: undefined });
    } else {
      updateSearch({ runs: next.join(',') });
    }
  }, [selectedRuns, availableRunNumbers, updateSearch]);

  const toggleWorkflow = React.useCallback((name: string) => {
    let next: string[];
    if (isAllWorkflows) {
      next = workflowNames.filter((n) => n !== name);
    } else if (selectedWorkflowSet.has(name)) {
      next = selectedWorkflows.filter((n) => n !== name);
    } else {
      next = [...selectedWorkflows, name];
    }
    // Empty or full selection both collapse to the implicit "all" state.
    updateSearch({
      workflows: next.length === 0 || next.length === workflowNames.length ? undefined : next,
    });
  }, [isAllWorkflows, workflowNames, selectedWorkflowSet, selectedWorkflows, updateSearch]);

  const selectAllWorkflows = React.useCallback(
    () => updateSearch({ workflows: undefined }),
    [updateSearch],
  );

  if (trendsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-10">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (allTrends.length === 0) {
    return <ChartEmptyState message="No trend data available. Run a workflow suite to see trends." />;
  }

  return (
    <Box className="space-y-6">
      {/* Filters */}
      <Box className="space-y-4">
        <Flex gap="md" align="center" wrap="wrap">
          <Select
            placeholder="Time range"
            items={DAYS_OPTIONS}
            selectedKey={String(days)}
            onSelectionChange={(key) => updateSearch({ days: Number(key) })}
            className="w-[140px]"
          />
          <Select
            placeholder="Trigger type"
            items={TRIGGER_OPTIONS}
            selectedKey={triggerFilter}
            onSelectionChange={(key) => updateSearch({ trigger: key === 'all' ? undefined : String(key) })}
            className="w-[150px]"
          />
          {workflowSuiteConfigs.length > 1 && (
            <Select
              placeholder="Configuration"
              items={[
                { id: 'all', label: 'All configs' },
                ...workflowSuiteConfigs.map((c) => ({ id: c.id, label: c.name })),
              ]}
              selectedKey={configFilter}
              onSelectionChange={(key) => updateSearch({ configId: key === 'all' ? undefined : String(key) })}
              className="min-w-[180px]"
            />
          )}
          {availableRunNumbers.length > 1 && (
            <Flex gap="xs" align="center">
              <span className="text-xs text-muted-foreground mr-1">Runs:</span>
              {availableRunNumbers.map((n) => {
                const isActive = selectedRuns.length === 0 || selectedRuns.includes(n);
                return (
                  <Button
                    key={n}
                    size="sm"
                    variant={isActive ? 'primary' : 'outline'}
                    onPress={() => toggleRunNumber(n)}
                    className="min-w-[2rem]"
                  >
                    {n}
                  </Button>
                );
              })}
            </Flex>
          )}
        </Flex>

        {workflowNames.length > 0 && (
          <WorkflowColorSelect
            workflows={workflowNames}
            colorMap={colorMap}
            selected={selectedWorkflowSet}
            allSelected={isAllWorkflows}
            onToggle={toggleWorkflow}
            onSelectAll={selectAllWorkflows}
          />
        )}
      </Box>

      {trends.length === 0 ? (
        <ChartEmptyState message="No trend data for selected filters." />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Duration over time */}
        <Card padding="md">
          <CardContent>
            <Heading level={5} className="mb-4">Duration (seconds)</Heading>
            {durationData.length > 0 ? (
              <LineChart
                data={durationData}
                xDataKey="date"
                lines={lines}
                height={250}
                showLegend={false}
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No duration data for selected filters." />
            )}
          </CardContent>
        </Card>

        {/* Token usage over time */}
        <Card padding="md">
          <CardContent>
            <Heading level={5} className="mb-4">Token Usage (M)</Heading>
            {tokenData.length > 0 ? (
              <LineChart
                data={tokenData}
                xDataKey="date"
                lines={lines}
                height={250}
                showLegend={false}
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No token data for selected filters." />
            )}
          </CardContent>
        </Card>

        <StackedChartCard
          title="Pass / Fail by Run"
          result={statusResult}
          stacks={statusStacks}
          legendItems={[
            { color: COLOR_SUCCESS, label: 'Passed' },
            { color: COLOR_FAIL, label: 'Failed' },
          ]}
          emptyMessage="No status data available."
          onBarClick={(data) => updateSearch({ suiteId: String(data.benchmarkRunId) })}
          renderTooltip={(data) => (
            <StatusTooltip data={data} maxRunNumber={statusResult.maxRunNumber} />
          )}
        />

        <StackedChartCard
          title="Node Results by Run"
          result={nodeResult}
          stacks={nodeStacks}
          legendItems={NODE_CATEGORIES}
          emptyMessage="No node data available."
          onBarClick={(data) => updateSearch({ suiteId: String(data.benchmarkRunId) })}
          renderTooltip={(data) => (
            <CategoryTooltip data={data} maxRunNumber={nodeResult.maxRunNumber} categories={NODE_CATEGORIES} />
          )}
        />

        <StackedChartCard
          title="Cached Steps by Run"
          result={cachedStepsResult}
          stacks={cachedStepsStacks}
          legendItems={CACHE_CATEGORIES}
          emptyMessage="No cached steps data available."
          onBarClick={(data) => updateSearch({ suiteId: String(data.benchmarkRunId) })}
          renderTooltip={(data) => (
            <CategoryTooltip data={data} maxRunNumber={cachedStepsResult.maxRunNumber} categories={CACHE_CATEGORIES} />
          )}
        />
      </div>
      )}
    </Box>
  );
}
